import React from 'react';
import Card from './Card';
import Badge from './Badge';
import { useLowStockQuery, useDeadStockQuery } from '../../../../api/dashboard.api';

/**
 * Low Stock & Dead Stock inventory alert component
 */
const AlertsSection = ({ threshold = 5, days = 30 }) => {
    const { data: lowStockData, isLoading: lowLoading } = useLowStockQuery({ threshold });
    const { data: deadStockData, isLoading: deadLoading } = useDeadStockQuery({ days });

    const lowStockItems = lowStockData?.result || lowStockData?.data || [];
    const deadStockItems = deadStockData?.result || deadStockData?.data || [];

    const lowCount = Array.isArray(lowStockItems) ? lowStockItems.length : 0;
    const deadCount = Array.isArray(deadStockItems) ? deadStockItems.length : 0;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full min-w-0">
            {/* Low Stock Alert Card */}
            <Card
                title="Low Stock Alert"
                subtitle={`Stock ≤ ${threshold}`}
                action={
                    <Badge variant={lowCount > 0 ? 'warning' : 'success'}>
                        {lowCount} {lowCount === 1 ? 'item' : 'items'}
                    </Badge>
                }
            >
                {lowLoading ? (
                    <div className="space-y-3">
                        <div className="h-10 bg-stone-100 animate-pulse rounded-xl" />
                        <div className="h-10 bg-stone-100 animate-pulse rounded-xl" />
                    </div>
                ) : lowCount === 0 ? (
                    <div className="py-6 text-center text-xs text-stone-400 font-medium">
                        ✓ All stock levels are sufficient
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100 max-h-64 overflow-y-auto pr-1">
                        {lowStockItems.map((item, idx) => {
                            const name = item.title || item.name || item.productName || `Product #${item._id?.slice(-5)}`;
                            const qty = item.quantity ?? item.stock ?? item.inventory ?? 0;

                            return (
                                <div
                                    key={item._id || item.id || idx}
                                    className="py-2.5 flex items-center justify-between text-xs hover:bg-stone-50/60 rounded-lg px-2 transition-colors min-w-0"
                                >
                                    <div className="min-w-0 pr-2">
                                        <p className="font-semibold text-stone-800 truncate">{name}</p>
                                        <p className="text-[11px] text-stone-400 truncate">
                                            ID: {item._id ? item._id.slice(-6) : '-'}
                                        </p>
                                    </div>
                                    <Badge variant={qty === 0 ? 'danger' : 'warning'}>
                                        {qty} left
                                    </Badge>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Dead Stock Alert Card */}
            <Card
                title="Dead Stock Alert"
                subtitle={`No sales in ${days}+ days`}
                action={
                    <Badge variant={deadCount > 0 ? 'danger' : 'neutral'}>
                        {deadCount} {deadCount === 1 ? 'item' : 'items'}
                    </Badge>
                }
            >
                {deadLoading ? (
                    <div className="space-y-3">
                        <div className="h-10 bg-stone-100 animate-pulse rounded-xl" />
                        <div className="h-10 bg-stone-100 animate-pulse rounded-xl" />
                    </div>
                ) : deadCount === 0 ? (
                    <div className="py-6 text-center text-xs text-stone-400 font-medium">
                        ✓ No dead stock detected
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100 max-h-64 overflow-y-auto pr-1">
                        {deadStockItems.map((item, idx) => {
                            const name = item.title || item.name || item.productName || `Product #${item._id?.slice(-5)}`;
                            const qty = item.quantity ?? item.stock ?? item.inventory ?? 0;

                            return (
                                <div
                                    key={item._id || item.id || idx}
                                    className="py-2.5 flex items-center justify-between text-xs hover:bg-stone-50/60 rounded-lg px-2 transition-colors min-w-0"
                                >
                                    <div className="min-w-0 pr-2">
                                        <p className="font-semibold text-stone-800 truncate">{name}</p>
                                        <p className="text-[11px] text-stone-400 truncate">
                                            Stock: {qty} units
                                        </p>
                                    </div>
                                    <Badge variant="danger">
                                        Idle ({days}+ d)
                                    </Badge>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default AlertsSection;
